import * as React from 'react'

import { Box, Button } from '@factorialco/deprecated-design-system'

import { observer } from 'mobx-react'

import Loading from 'modules/core/components/Loading'
import Modal from 'modules/core/components/Modal'
import Steps from 'modules/core/components/Steps'
// Hooks
import { useAsyncData } from 'modules/core/hooks/useAsyncData'
import i18n from 'modules/core/lib/i18n'

import usePlanName from 'modules/billing/hooks/usePlanName'
import cards from 'modules/billing/store/collections/cards'
import plans from 'modules/billing/store/collections/plans'
import subscriptions from 'modules/billing/store/collections/subscriptions'
import type { Families } from 'modules/billing/store/models/Plan'

import Bill from './Bill'
import BillingInfoStep from './BillingInfoStep'
import useBillingInfo from './BillingInfoStep/useBillingInfo'
import CreditCardStep from './CreditCardStep'
import useCardValidation from './CreditCardStep/useCardValidation'
import useCreateSubscription from './useCreateSubscription'

interface Props {
  family: Families
  isYearly: boolean
  onClose: () => void
  onPaymentActionRequired: () => void
  onPaymentSuccessful: () => void
}

const CARD_STEP = 1

const PaymentModal = ({
  family,
  isYearly,
  onClose,
  onPaymentActionRequired,
  onPaymentSuccessful,
}: Props) => {
  const [step, setStep] = React.useState(1)

  const onFail = () => {
    setStep(CARD_STEP)
  }

  const onBack = () => {
    setStep(step - 1)
  }

  // Load Data
  const { loading } = useAsyncData(
    () => Promise.all([plans.fetch(), cards.fetch(), subscriptions.fetch()]),
    []
  )

  // Subscription creation
  const {
    selectedPlan,
    subscriptionForm,
    createSubscription,
    onChangePricingPeriod,
    onChangePricingPlan,
  } = useCreateSubscription({
    loading,
    family,
    isYearly,
    onFail,
    onPaymentActionRequired,
    onPaymentSuccessful,
  })

  const planName = usePlanName(selectedPlan?.family)

  // Validate card
  const onValidateCard = async () => {
    const { value: isValid } = await validateCard()

    if (isValid) setStep(step + 1)
  }

  const { validatingCard, validateCard } = useCardValidation({
    form: subscriptionForm,
  })

  // Billing info
  const billingInfo = useBillingInfo({
    createSubscription,
    onFail,
  })

  const subscription = subscriptions.current
  const currentSeats = subscription.attributes.current_cycle_quantity

  const description =
    step === 1
      ? i18n.t('billing.payment.steps.1.description')
      : i18n.t('billing.payment.steps.2.description')

  const title = planName
    ? i18n.t('billing.payment.modal_title', {
        planName,
      })
    : i18n.t('pricing_plans.generic_upgrade')

  return (
    <Modal
      illustration="fireworks"
      title={title}
      description={description}
      onClose={onClose}
      footer={
        <>
          {step !== 1 && (
            <Button type="secondary" onClick={onBack}>
              {i18n.t('common.back')}
            </Button>
          )}

          {step === 1 && (
            <Button disabled={validatingCard} onClick={onValidateCard}>
              {i18n.t('billing.payment.steps.continue')}
            </Button>
          )}

          {step === 2 && (
            <Button disabled={billingInfo.submitting} onClick={billingInfo.onSubmit}>
              {i18n.t('billing.payment.confirm')}
            </Button>
          )}
        </>
      }
    >
      <Box gap="s24">
        <Steps separator step={step} steps={2} />

        {loading && <Loading />}

        {!loading && selectedPlan && (
          <Bill
            planIntervalReadonly={false}
            plan={selectedPlan}
            planName={planName}
            currentSeats={currentSeats}
            onTogglePricingPeriod={onChangePricingPeriod}
            onChangePricingPlan={onChangePricingPlan}
          />
        )}

        {!loading && (
          <>
            {step === 1 && <CreditCardStep form={subscriptionForm} />}
            {step === 2 && (
              <BillingInfoStep
                addressForm={billingInfo.addressForm}
                identificationForm={billingInfo.identificationForm}
                legalEntityForm={billingInfo.legalEntityForm}
              />
            )}
          </>
        )}
      </Box>
    </Modal>
  )
}

export default observer(PaymentModal)
